const RECOVERY_QUERY_PARAMETER = "__thread_reload";
const RECOVERY_STORAGE_KEY = "thread:stale-asset-recovery-at";

export const RUNTIME_GENERATION_FILE = "thread-runtime.json";
export const RUNTIME_GENERATION_META_NAME = "thread-runtime-generation";

export const STALE_ASSET_RECOVERY_COOLDOWN_MS = 30_000;

interface RecoveryLocation {
  href: string;
  replace(url: string): void;
}

interface RecoveryHistory {
  readonly state: unknown;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

interface RecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface StaleAssetRecoveryRuntime {
  readonly location: RecoveryLocation;
  readonly history: RecoveryHistory;
  readonly sessionStorage?: RecoveryStorage;
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

export interface StaleAssetRecoveryOptions {
  readonly runtime?: StaleAssetRecoveryRuntime;
  readonly now?: () => number;
  readonly recoverPageCache?: boolean;
  readonly shouldRecoverPageCache?: () => boolean | Promise<boolean>;
}

const LOCAL_DEMO_HOSTNAMES = new Set([
  "127.0.0.1",
  "localhost",
  "::1",
  "[::1]",
  "thread-local.simulation.cloud",
]);

export function isLocalDemoHostname(hostname: string): boolean {
  return LOCAL_DEMO_HOSTNAMES.has(hostname.toLowerCase());
}

export async function runtimeGenerationChanged(
  currentGeneration: string,
  manifestUrl: string,
  fetcher: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const response = await fetcher(manifestUrl, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!response.ok) return false;
    const value: unknown = await response.json();
    if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
    const fields = Object.keys(value);
    if (fields.length !== 1 || fields[0] !== "generation") return false;
    const generation = (value as { generation?: unknown }).generation;
    return typeof generation === "string" && generation.length > 0
      && generation !== currentGeneration;
  } catch {
    // A failed freshness probe must not discard an in-memory Loom draft.
    return false;
  }
}

function finiteTimestamp(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function readStoredAttempt(runtime: StaleAssetRecoveryRuntime): number | null {
  try {
    return finiteTimestamp(runtime.sessionStorage?.getItem(RECOVERY_STORAGE_KEY) ?? null);
  } catch {
    // Safari private windows can deny storage even when the property exists.
    return null;
  }
}

function storeAttempt(runtime: StaleAssetRecoveryRuntime, timestamp: number): void {
  try {
    runtime.sessionStorage?.setItem(RECOVERY_STORAGE_KEY, String(timestamp));
  } catch {
    // The URL marker below still provides loop protection after navigation.
  }
}

function consumeRecoveryMarker(runtime: StaleAssetRecoveryRuntime): number | null {
  const current = new URL(runtime.location.href);
  const timestamp = finiteTimestamp(current.searchParams.get(RECOVERY_QUERY_PARAMETER));
  if (!current.searchParams.has(RECOVERY_QUERY_PARAMETER)) return timestamp;

  current.searchParams.delete(RECOVERY_QUERY_PARAMETER);
  runtime.history.replaceState(
    runtime.history.state,
    "",
    `${current.pathname}${current.search}${current.hash}`,
  );
  return timestamp;
}

/**
 * Recover a long-lived tab from a stale Vite chunk or Safari page-cache entry.
 *
 * The fresh navigation gets a unique document URL, then the marker is removed
 * as soon as the new runtime starts. A timestamp in both the URL and session
 * storage prevents a persistent network/build failure from becoming a reload
 * loop, including when Safari private browsing refuses storage access.
 */
export function installStaleAssetRecovery(
  options: StaleAssetRecoveryOptions = {},
): () => void {
  const runtime = options.runtime
    ?? (window as unknown as StaleAssetRecoveryRuntime);
  const now = options.now ?? Date.now;
  const recoverPageCache = options.recoverPageCache ?? false;
  const shouldRecoverPageCache = options.shouldRecoverPageCache;
  let lastAttemptAt = Math.max(
    consumeRecoveryMarker(runtime) ?? -1,
    readStoredAttempt(runtime) ?? -1,
  );

  const attemptRecovery = (): boolean => {
    const timestamp = now();
    if (timestamp - lastAttemptAt < STALE_ASSET_RECOVERY_COOLDOWN_MS) return false;

    lastAttemptAt = timestamp;
    storeAttempt(runtime, timestamp);
    const freshUrl = new URL(runtime.location.href);
    freshUrl.searchParams.set(RECOVERY_QUERY_PARAMETER, String(timestamp));
    runtime.location.replace(freshUrl.toString());
    return true;
  };

  const onPreloadError: EventListener = (event) => {
    if (attemptRecovery()) event.preventDefault();
  };
  const onPageShow: EventListener = (event) => {
    const restoredFromPageCache = "persisted" in event
      && Boolean((event as Event & { persisted: unknown }).persisted);
    if (!recoverPageCache || !restoredFromPageCache) return;
    if (!shouldRecoverPageCache) {
      attemptRecovery();
      return;
    }
    void Promise.resolve(shouldRecoverPageCache())
      .then((stale) => {
        if (stale) attemptRecovery();
      })
      .catch(() => undefined);
  };

  runtime.addEventListener("vite:preloadError", onPreloadError);
  runtime.addEventListener("pageshow", onPageShow);
  return () => {
    runtime.removeEventListener("vite:preloadError", onPreloadError);
    runtime.removeEventListener("pageshow", onPageShow);
  };
}
